<template>
    <div class="form-group mb-0">
        <label :for="id" class="field-label">{{ label }}</label>

        <input
            :id="id"
            ref="fileInput"
            type="file"
            class="sr-only"
            :accept="acceptedFiles"
            :disabled="uploading || disabled"
            @change="uploadFile"
        />

        <div
            class="upload-dropzone flex flex-col items-center justify-center text-center"
            :class="{ 'is-dragging': dragging, 'is-uploading': uploading, 'is-disabled': disabled }"
            role="button"
            :tabindex="disabled ? -1 : 0"
            :aria-disabled="uploading || disabled ? 'true' : 'false'"
            :aria-describedby="`${id}-upload-help`"
            @click="openFileDialog"
            @keydown.enter.prevent="openFileDialog"
            @keydown.space.prevent="openFileDialog"
            @dragenter.prevent="enterDropZone"
            @dragover.prevent="enterDropZone"
            @dragleave.prevent="leaveDropZone"
            @drop.prevent="dropFile"
        >
            <span class="upload-icon flex items-center justify-center" aria-hidden="true">
                <svg viewBox="0 0 24 24" focusable="false">
                    <path d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5M5 14v4a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4" />
                </svg>
            </span>
            <strong class="upload-title block mt-2">{{ dropzoneTitle }}</strong>
            <span :id="`${id}-upload-help`" class="block mt-1 text-xs text-pip-gray">
                {{ dropzoneHelp }}
            </span>
            <span v-if="!uploading" class="browse-action block mt-2 text-xs font-semibold">
                {{ value ? replaceLabel : uploadLabel }}
            </span>

            <div v-else class="upload-progress mt-3 w-full" role="progressbar" :aria-valuenow="uploadProgress" aria-valuemin="0" aria-valuemax="100">
                <span :style="{ width: `${uploadProgress}%` }"></span>
            </div>
        </div>

        <div v-if="value" class="current-file flex items-center gap-2 mt-2">
            <span class="file-type flex items-center justify-center flex-none" aria-hidden="true">
                <img v-if="uploadType === 'image' && previewUrl" :src="previewUrl" alt="" />
                <template v-else>{{ fileTypeLabel }}</template>
            </span>
            <span class="min-w-0 flex-1">
                <strong class="file-name block text-sm" :title="displayName">{{ displayName }}</strong>
                <a
                    v-if="previewUrl"
                    class="preview-link text-xs"
                    :href="previewUrl"
                    target="_blank"
                    rel="noopener"
                >
                    {{ previewLabel }} <span aria-hidden="true">↗</span>
                </a>
            </span>
            <button
                v-if="allowRemove"
                type="button"
                class="remove-file flex-none"
                :disabled="uploading || disabled"
                :aria-label="`Remove ${displayName}`"
                title="Remove attachment"
                @click="removeFile"
            >
                ×
            </button>
        </div>

        <small v-if="error" class="feedback-message is-error form-text mt-2" role="alert">{{ error }}</small>
        <small v-else-if="success" class="feedback-message is-success form-text mt-2" role="status">{{ success }}</small>
        <small v-else-if="help && !value" class="form-text text-pip-gray mt-2">{{ help }}</small>
    </div>
</template>

<script>
export default {
    name: "AdminFileUploadField",
    props: {
        id: { type: String, required: true },
        label: { type: String, required: true },
        value: { type: String, default: "" },
        uploadType: {
            type: String,
            required: true,
            validator: value => ["image", "pdf", "video"].includes(value),
        },
        endpoint: { type: String, default: "/api/admin/media" },
        previewUrl: { type: String, default: "" },
        previewLabel: { type: String, default: "View current file" },
        uploadLabel: { type: String, default: "Upload file" },
        replaceLabel: { type: String, default: "Replace" },
        help: { type: String, default: "" },
        maxUploadSizeMb: { type: Number, default: 50 },
        allowRemove: { type: Boolean, default: true },
        disabled: { type: Boolean, default: false },
    },
    data() {
        return {
            uploading: false,
            uploadProgress: 0,
            dragging: false,
            error: null,
            success: null,
        };
    },
    computed: {
        acceptedFiles() {
            const accepted = {
                image: "image/jpeg,image/png,image/gif,image/webp",
                pdf: "application/pdf,.pdf",
                video: "video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov,.m4v",
            };
            return accepted[this.uploadType];
        },
        fileTypeLabel() {
            return { image: "IMG", pdf: "PDF", video: "VID" }[this.uploadType];
        },
        displayName() {
            const parts = this.value.split("/");
            return decodeURIComponent(parts[parts.length - 1] || this.value);
        },
        dropzoneTitle() {
            if (this.uploading) return `Uploading ${this.uploadProgress}%`;
            if (this.dragging) return "Release to upload";
            if (this.value) return "Drop a replacement here";
            return {
                image: "Drop an image here",
                pdf: "Drop a PDF here",
                video: "Drop a video here",
            }[this.uploadType];
        },
        dropzoneHelp() {
            const typeLabel = {
                image: "JPG, PNG, GIF or WebP",
                pdf: "PDF",
                video: "MP4, WebM, MOV or M4V",
            }[this.uploadType];
            return `${typeLabel} · maximum ${this.maxUploadSizeMb} MB`;
        },
    },
    methods: {
        openFileDialog() {
            if (!this.uploading && !this.disabled) this.$refs.fileInput.click();
        },
        enterDropZone() {
            if (!this.uploading && !this.disabled) this.dragging = true;
        },
        async uploadFile(event) {
            const file = event.target.files[0];
            await this.processFile(file, event.target);
        },
        async dropFile(event) {
            this.dragging = false;
            if (this.uploading || this.disabled) return;

            const files = event.dataTransfer && event.dataTransfer.files;
            if (!files || !files.length) return;
            if (files.length > 1) {
                this.error = "Please upload one file at a time.";
                this.success = null;
                return;
            }

            await this.processFile(files[0]);
        },
        leaveDropZone(event) {
            if (!event.currentTarget.contains(event.relatedTarget)) this.dragging = false;
        },
        async processFile(file, input = null) {
            if (!file) return;

            this.error = null;
            this.success = null;

            if (!this.isAcceptedFile(file)) {
                const messages = {
                    image: "Please choose a JPG, PNG, GIF, or WebP image.",
                    pdf: "Please choose a PDF file.",
                    video: "Please choose an MP4, WebM, MOV, or M4V video.",
                };
                this.error = messages[this.uploadType];
                if (input) input.value = "";
                return;
            }

            if (file.size > this.maxUploadSizeMb * 1024 * 1024) {
                this.error = `The selected file is larger than ${this.maxUploadSizeMb} MB.`;
                if (input) input.value = "";
                return;
            }

            const formData = new FormData();
            formData.append("file", file);
            formData.append("type", this.uploadType);

            this.uploading = true;
            this.uploadProgress = 0;

            try {
                const response = await axios.post(this.endpoint, formData, {
                    onUploadProgress: progressEvent => {
                        if (!progressEvent.total) return;
                        this.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    },
                });

                this.$emit("input", response.data.path);
                this.$emit("uploaded", response.data);
                this.success = `${file.name} uploaded and attached.`;
            } catch (error) {
                this.error = this.uploadErrorMessage(error);
                this.$emit("upload-error", error);
            } finally {
                this.uploading = false;
                this.uploadProgress = 0;
                if (input) input.value = "";
            }
        },
        isAcceptedFile(file) {
            if (this.uploadType === "pdf") {
                return file.type === "application/pdf" || /\.pdf$/i.test(file.name);
            }

            if (this.uploadType === "video") {
                return (
                    ["video/mp4", "video/webm", "video/quicktime", "video/x-m4v"].includes(file.type) ||
                    /\.(mp4|webm|mov|m4v)$/i.test(file.name)
                );
            }

            return (
                ["image/jpeg", "image/png", "image/gif", "image/webp"].includes(file.type) ||
                /\.(jpe?g|png|gif|webp)$/i.test(file.name)
            );
        },
        removeFile() {
            this.error = null;
            this.success = null;
            this.$emit("input", "");
            this.$emit("removed");
        },
        uploadErrorMessage(error) {
            const response = error.response;

            if (!response) return "The upload could not reach the server. Please try again.";
            if (response.status === 413) {
                return `The server rejected this file because it is larger than ${this.maxUploadSizeMb} MB.`;
            }

            const validationErrors = response.data && response.data.errors;
            if (validationErrors && validationErrors.file) return validationErrors.file[0];
            if (validationErrors && validationErrors.type) return validationErrors.type[0];

            return "The file could not be uploaded. Please verify the file type and try again.";
        },
    },
};
</script>

<style scoped lang="scss">
.field-label {
    display: block;
    margin-bottom: 0.35rem;
    color: var(--color-pip-gray);
    font-size: var(--font-size-xs);
    font-weight: var(--font-weight-bold);
    letter-spacing: var(--tracking-wider);
    text-transform: uppercase;
}

.upload-dropzone {
    min-height: 8.5rem;
    padding: 1rem;
    border: 2px dashed var(--color-light-grey);
    border-radius: 0.25rem;
    background: var(--color-grey-100);
    cursor: pointer;
    outline: 0;
    transition: border-color 120ms ease, background-color 120ms ease, box-shadow 120ms ease;

    &:hover,
    &:focus {
        border-color: var(--color-primary);
        background: #fff9f8;
        box-shadow: 0 0 0 3px rgba(205, 27, 0, 0.1);
    }

    &.is-dragging {
        border-color: var(--color-primary);
        background: #fff1ef;
        box-shadow: inset 0 0 0 1px var(--color-primary);
    }

    &.is-disabled {
        opacity: 0.65;
        cursor: not-allowed;
    }

    &.is-uploading {
        cursor: wait;
    }
}

.upload-icon {
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 50%;
    color: var(--color-primary);
    background: #fbe8e5;

    svg {
        width: 1.15rem;
        height: 1.15rem;
        fill: none;
        stroke: currentColor;
        stroke-linecap: round;
        stroke-linejoin: round;
        stroke-width: 1.75;
    }
}

.upload-title {
    color: var(--color-mine-shaft);
    font-size: var(--font-size-sm);
}

.browse-action,
.preview-link {
    color: var(--color-primary);
}

.browse-action {
    text-decoration: underline;
}

.upload-progress {
    max-width: 14rem;
    height: 0.3rem;
    overflow: hidden;
    border-radius: 999px;
    background: var(--color-light-grey);

    span {
        display: block;
        height: 100%;
        background: var(--color-primary);
        transition: width 120ms ease;
    }
}

.current-file {
    min-height: 3rem;
    padding: 0.45rem 0.55rem;
    border: 1px solid var(--color-light-grey);
    background: #fff;
}

.file-type {
    width: 2rem;
    height: 2rem;
    color: #fff;
    background: var(--color-primary);
    font-size: 0.58rem;
    font-weight: var(--font-weight-bold);
    letter-spacing: 0.04em;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
}

.file-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.remove-file {
    width: 1.75rem;
    height: 1.75rem;
    padding: 0;
    border: 0;
    border-radius: 50%;
    color: var(--color-pip-gray);
    background: transparent;
    font-size: 1.25rem;
    line-height: 1;

    &:hover,
    &:focus {
        color: var(--color-primary);
        background: #fbe8e5;
        outline: 0;
    }
}

.feedback-message {
    padding: 0.35rem 0.5rem;
    border-left: 3px solid currentColor;

    &.is-error {
        color: var(--color-primary);
        background: #fff1f2;
    }

    &.is-success {
        color: #176527;
        background: #edf8ef;
    }
}
</style>
